<template>
  <footer data-nosnippet>
    <div class="saas-footer">
      <div class="saas-footer__separator"></div>
      <div class="saas-footer__inner">
        <div class="saas-footer__content">
          <div class="saas-footer__group">
            <div class="saas-footer__group-top">
              <NuxtLink class="saas-footer__logo" :to="{ name: 'index' }">
                <img
                  src="@baserow/modules/core/static/img/logo-white.svg?url"
                  alt="Baserow logo"
                  data-allow-mismatch
                />
              </NuxtLink>
            </div>
            <div class="saas-footer__group-title">Company</div>
            <ul class="saas-footer__links">
              <li v-for="aboutUsPage in aboutUsPages" :key="aboutUsPage.path">
                <NuxtLink :to="aboutUsPage.path">
                  {{ aboutUsPage.title }}
                </NuxtLink>
              </li>
              <li
                v-for="jobListingPage in jobListingPages"
                :key="jobListingPage.path"
              >
                <NuxtLink :to="jobListingPage.path">
                  {{ jobListingPage.title }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink :to="{ name: 'faq' }">FAQ</NuxtLink>
              </li>
              <li>
                <NuxtLink :to="{ name: 'partners' }">Partners</NuxtLink>
              </li>
              <li>
                <NuxtLink :to="{ name: 'contact' }">Contact</NuxtLink>
              </li>
              <li>
                <a
                  href="https://status.baserow.org/"
                  rel="noopener noreferrer"
                  target="_blank"
                  >Status</a
                >
              </li>
            </ul>
          </div>
          <div class="saas-footer__group">
            <div class="saas-footer__group-title">Platform</div>
            <ul class="saas-footer__links">
              <li>
                <NuxtLink :to="{ name: 'templates' }">Templates</NuxtLink>
              </li>
              <li
                v-for="enterprisePage in enterprisePages"
                :key="enterprisePage.path"
              >
                <NuxtLink :to="enterprisePage.path" class="saas-footer__link">
                  {{ enterprisePage.title }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink :to="{ name: 'pricing' }">Pricing</NuxtLink>
              </li>
              <li
                v-for="communityPage in communityPages"
                :key="communityPage.path"
              >
                <NuxtLink :to="communityPage.path">
                  {{ communityPage.title }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink :to="{ name: 'user-docs' }">Knowledge base</NuxtLink>
              </li>
              <li>
                <a
                  href="https://n8n.io/integrations/baserow/"
                  title="Baserow n8n integration"
                  rel="noopener noreferrer"
                  target="_blank"
                  >n8n integration</a
                >
              </li>
              <li>
                <a
                  href="https://zapier.com/apps/baserow/integrations"
                  title="Baserow Zapier integration"
                  rel="noopener noreferrer"
                  target="_blank"
                  >Zapier integration</a
                >
              </li>
              <li>
                <a
                  href="https://www.make.com/en/integrations/baserow"
                  title="Baserow Make integration"
                  rel="noopener noreferrer"
                  target="_blank"
                  >Make integration</a
                >
              </li>
              <li
                v-for="integrationPage in integrationPages"
                :key="integrationPage.path"
              >
                <NuxtLink :to="integrationPage.path" class="saas-footer__link">
                  All integrations
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="saas-footer__group">
            <div class="saas-footer__group-title">Blog</div>
            <ul class="saas-footer__links">
              <li
                v-for="summary in highlightedBlogSummaries"
                :key="summary.slug"
              >
                <NuxtLink
                  :to="{
                    name: 'blog-post-page',
                    params: {
                      blogPostSlug: summary.slug,
                    },
                  }"
                  >{{ summary.short_title }}</NuxtLink
                >
              </li>
            </ul>
          </div>
          <div class="saas-footer__group">
            <div class="saas-footer__group-top">
              <a
                href="https://github.com/baserow/baserow"
                title="Baserow repository at GitHub"
                rel="noopener noreferrer"
                target="_blank"
                class="saas-footer__social"
              >
                <i class="iconoir-github"></i>
              </a>
              <a
                href="https://twitter.com/baserow"
                title="Baserow Twitter profile"
                rel="noopener noreferrer"
                target="_blank"
                class="saas-footer__social"
              >
                <i class="baserow-icon-twitter"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/baserow/"
                title="Baserow LinkedIn profile"
                rel="noopener noreferrer"
                target="_blank"
                class="saas-footer__social"
              >
                <i class="baserow-icon-linkedin"></i>
              </a>
              <a
                href="https://community.baserow.io/"
                title="Baserow community forum"
                rel="noopener noreferrer"
                target="_blank"
                class="saas-footer__social"
              >
                <i class="iconoir-chat-bubble-question"></i>
              </a>
            </div>
            <div class="saas-footer__group-title">For developers</div>
            <ul class="saas-footer__links">
              <li>
                <NuxtLink
                  :to="{
                    name: 'dev-docs',
                    params: {
                      path: 'index',
                    },
                  }"
                  >Documentation</NuxtLink
                >
              </li>
              <li>
                <NuxtLink
                  :to="{ name: 'database-api-docs' }"
                  class="saas-footer__link"
                  >API</NuxtLink
                >
              </li>
              <li>
                <a :href="publicBackendUrl + '/api/redoc/'" target="_blank"
                  >OpenAPI</a
                >
              </li>
            </ul>
            <div class="saas-footer__group-title margin-top-4 margin-bottom-1">
              Join our newsletter
            </div>
            <div class="saas-footer__group-description">
              Stay up to date with the latest developments and releases by
              signing up for our newsletter.
            </div>
            <NewsletterSignup :dark="true"></NewsletterSignup>
          </div>
        </div>
      </div>
      <div class="saas-footer__separator"></div>
      <div class="saas-footer__inner">
        <div class="saas-footer__legal">
          <div>
            &copy; {{ new Date().getFullYear() }} Baserow All rights reserved.
          </div>
          <div>
            <NuxtLink :to="{ name: 'terms' }">Terms & conditions</NuxtLink>
            <NuxtLink :to="{ name: 'privacy' }">Privacy policy</NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { useRuntimeConfig, useNuxtApp } from '#imports'

import NewsletterSignup from '@saas/components/NewsletterSignup'

export default {
  components: { NewsletterSignup },
  setup() {
    const config = useRuntimeConfig()
    const { $store } = useNuxtApp()

    // Safe getter helper that returns empty array if store is not ready
    const safeGetMenuPagesPerType = (type) => {
      if (!$store || !$store.hasModule('saasLayout')) return []
      return $store.getters['saasLayout/getMenuPagesPerType'](type) || []
    }

    return {
      publicBackendUrl: config.public.publicBackendUrl,
      store: $store,
      safeGetMenuPagesPerType,
    }
  },
  computed: {
    enterprisePages() {
      return this.safeGetMenuPagesPerType('saas_cms.EnterprisePage')
    },
    aboutUsPages() {
      return this.safeGetMenuPagesPerType('saas_cms.AboutUsPage')
    },
    jobListingPages() {
      return this.safeGetMenuPagesPerType('saas_cms.JobsListingPage')
    },
    communityPages() {
      return this.safeGetMenuPagesPerType('saas_cms.CommunityPage')
    },
    integrationPages() {
      return this.safeGetMenuPagesPerType('saas_cms.IntegrationsPage')
    },
    isAuthenticated() {
      if (!this.store || !this.store.hasModule('auth')) return false
      return this.store.getters['auth/isAuthenticated']
    },
    highlightedBlogSummaries() {
      if (!this.store || !this.store.hasModule('saasLayout')) return []
      return this.store.getters['saasLayout/getHighlighted'] || []
    },
  },
}
</script>
